<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
import { computed, nextTick, ref, watch } from 'vue';
import QprecoInterestsAPI from 'dashboard/api/qprecoInterests';
import PriceRangeRent from '../PriceRangeRent.vue';
import PriceRangeSale from '../PriceRangeSale.vue';
import LaisPrimaryButton from '../LaisPrimaryButton.vue';
import PriceRuler from './PriceRuler.vue';
import PricingReportSection from './PricingReportSection.vue';
import {
  buildPriceRulerState,
  numberValue,
  resolveRulerVariant,
} from 'dashboard/helper/lais/priceRuler';

const props = defineProps({
  interestId: { type: [Number, String], required: true },
  initialSection: { type: String, default: '' },
});

const emit = defineEmits(['loaded', 'sectionViewed']);

const report = ref(null);
const loading = ref(false);
const error = ref(false);

const formatCurrency = value => {
  const number = numberValue(value);
  if (!number) return 'Sem dados';

  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  }).format(number);
};

const formatNumber = value =>
  new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 0 }).format(
    numberValue(value)
  );

const formatDate = value => {
  if (!value) return 'Data não informada';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);

  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(date);
};

const reportSectionId = key => `pricing-report-section-${key}`;

const scrollToInitialSection = async () => {
  if (!props.initialSection || typeof document === 'undefined') return;
  await nextTick();
  document
    .getElementById(reportSectionId(props.initialSection))
    ?.scrollIntoView({
      block: 'start',
      behavior: 'smooth',
    });
};

const fetchReport = async () => {
  if (!props.interestId) return;

  loading.value = true;
  error.value = false;
  report.value = null;

  try {
    const { data } = await QprecoInterestsAPI.report(props.interestId);
    report.value = data;
    emit('loaded', data);
    scrollToInitialSection();
  } catch {
    error.value = true;
  } finally {
    loading.value = false;
  }
};

watch(() => props.interestId, fetchReport, { immediate: true });
watch(() => props.initialSection, scrollToInitialSection);

const publicationType = computed(() => report.value?.publication_type || '');
const rulerVariant = computed(() => resolveRulerVariant(publicationType.value));
const isRentPublication = computed(() => rulerVariant.value === 'rent');
const priceRangeComponent = computed(() =>
  isRentPublication.value ? PriceRangeRent : PriceRangeSale
);
const hasInsufficientData = computed(
  () => report.value?.confidence_level === 'insufficient_data'
);
const cmaReport = computed(() => report.value?.cma_report || {});
const cmaLatestReport = computed(() => cmaReport.value?.latest_report || {});
const cmaPercentiles = computed(
  () => cmaReport.value?.percentiles || cmaLatestReport.value?.percentiles || {}
);
const cmaComparables = computed(() =>
  Array.isArray(cmaReport.value?.comparables) ? cmaReport.value.comparables : []
);
const cmaVersions = computed(() =>
  Array.isArray(cmaReport.value?.versions) ? cmaReport.value.versions : []
);
const cmaDisclaimers = computed(() =>
  Array.isArray(cmaReport.value?.disclaimers) ? cmaReport.value.disclaimers : []
);
const hasCmaReport = computed(() =>
  Boolean(cmaReport.value?.case_uid || cmaLatestReport.value?.id)
);
const cmaCriteriaEntries = computed(() => {
  const criteria = cmaReport.value?.selection_criteria || {};
  return [
    ['Modalidade', criteria.modality],
    ['Data-base', criteria.base_date],
    ['Amostra mínima', criteria.min_samples],
    ['Janela', criteria.max_age_days ? `${criteria.max_age_days} dias` : null],
    ['Tolerância área', criteria.area_tolerance],
    ['Delta quartos', criteria.bedroom_delta],
    ['Limite candidatos', criteria.candidate_limit],
  ]
    .filter(
      ([, value]) => value !== null && value !== undefined && value !== ''
    )
    .map(([label, value]) => ({ label, value }));
});
const cmaConfidenceLabel = computed(() => {
  const level =
    cmaReport.value?.confidence?.level ||
    cmaLatestReport.value?.confidence_snapshot?.level ||
    'none';
  return (
    {
      high: 'Alta confiança',
      medium: 'Confiança média',
      low: 'Baixa confiança',
      none: 'Sem confiança',
    }[level] || level
  );
});

const rulerState = computed(() =>
  buildPriceRulerState({
    value: report.value?.main_marker_price,
    min: report.value?.min_price,
    max: report.value?.max_price,
    percentiles: report.value?.percentiles,
    markerColor: report.value?.main_marker_color,
    variant: rulerVariant.value,
  })
);

const filterTags = computed(() => {
  const address = report.value?.address || {};
  return [
    address.type ? { key: 'type', label: address.type } : null,
    address.bedrooms
      ? { key: 'bedrooms', label: `${formatNumber(address.bedrooms)} quartos` }
      : null,
    address.area
      ? { key: 'area', label: `${formatNumber(address.area)} m²` }
      : null,
    address.neighborhood
      ? { key: 'neighborhood', label: address.neighborhood }
      : null,
    report.value?.comparables_count
      ? {
          key: 'comparables',
          label: `${formatNumber(report.value.comparables_count)} comparáveis`,
        }
      : null,
  ].filter(Boolean);
});

const similarProperties = computed(() =>
  Array.isArray(report.value?.similar_properties)
    ? report.value.similar_properties
    : []
);
const medianCards = computed(() =>
  Array.isArray(report.value?.median_cards) ? report.value.median_cards : []
);
const propertyHistory = computed(() =>
  Array.isArray(report.value?.property_history)
    ? report.value.property_history
    : []
);
const showEarningEstimated = computed(
  () => isRentPublication.value && numberValue(report.value?.earning_estimated)
);

const formattedMedianCard = card => {
  const label = String(card?.label || '').toLowerCase();
  const value = card?.value;

  if (label.includes('confiança')) {
    const labels = {
      high: 'Alta confiança',
      medium: 'Confiança média',
      low: 'Baixa confiança',
      insufficient_data: 'Dados limitados',
    };
    return labels[value] || value || 'Dados limitados';
  }

  if (label.includes('amostra')) return formatNumber(value);
  if (label.includes('m2') || label.includes('m²')) {
    const formatted = formatCurrency(value);
    return formatted === 'Sem dados' ? formatted : `${formatted}/m²`;
  }

  return formatCurrency(value);
};

const similarTitle = item =>
  item?.title ||
  item?.titulo ||
  item?.imovel_titulo ||
  item?.qpreco_card?.code ||
  'Imóvel similar';

const similarDetails = item =>
  [
    item?.bairro || item?.neighborhood,
    item?.area_util || item?.area
      ? `${formatNumber(item.area_util || item.area)} m²`
      : null,
    item?.quartos || item?.bedrooms
      ? `${formatNumber(item.quartos || item.bedrooms)} quartos`
      : null,
  ]
    .filter(Boolean)
    .join(' · ');

const similarPrice = item =>
  isRentPublication.value
    ? item?.valor_aluguel || item?.rent_price || item?.currentPrice
    : item?.valor_venda || item?.sale_price || item?.currentPrice;

const historyPrice = item =>
  item?.price ||
  item?.valor_venda ||
  item?.valor_aluguel ||
  item?.currentPrice ||
  item?.current_price;

const historySource = item =>
  item?.sourcePortal || item?.source_portal || item?.source || 'Imudei';

const historyKind = item =>
  item?.transactionLabel ||
  item?.transaction_label ||
  item?.kind ||
  'Histórico';

const historyDate = item =>
  item?.eventDate || item?.event_date || item?.recorded_at || item?.created_at;

const cmaComparablePrice = item =>
  item?.adjusted_price_amount ||
  item?.price_amount ||
  item?.observed_price_amount;

const cmaComparableSource = item =>
  item?.source || item?.source_license || 'Fonte não informada';

const cmaComparableDate = item =>
  item?.event_date || item?.eventDate || item?.selected_at;

const cmaComparableScore = item => {
  const number = numberValue(item?.comparability_score || item?.weight);
  if (!number) return 'Sem score';
  return `${Math.round(number * 100)}%`;
};

const handleSectionViewed = sectionKey => {
  emit('sectionViewed', sectionKey);
};
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <div class="grid gap-4">
    <div
      v-if="loading"
      aria-label="Carregando relatório de preço"
      class="grid gap-4"
    >
      <div
        class="rounded-lais border border-lais-border-soft bg-white p-4 animate-pulse"
      >
        <div class="h-4 w-40 rounded bg-lais-bg-muted" />
        <div class="mt-5 h-3 rounded-full bg-lais-bg-muted" />
        <div class="mt-4 grid gap-2 sm:grid-cols-3">
          <div
            v-for="index in 3"
            :key="index"
            class="h-16 rounded-lais-sm bg-lais-bg-muted"
          />
        </div>
      </div>
      <div class="grid gap-3 sm:grid-cols-3">
        <div
          v-for="index in 3"
          :key="`card-${index}`"
          class="h-24 rounded-lais border border-lais-border-soft bg-white animate-pulse"
        />
      </div>
      <div
        class="h-40 rounded-lais border border-lais-border-soft bg-white animate-pulse"
      />
    </div>

    <div
      v-else-if="error"
      class="rounded-lais border border-rose-100 bg-rose-50 p-4 text-[13px] text-rose-800"
    >
      <p class="font-semibold">Não foi possível carregar o relatório.</p>
      <p class="mt-1">
        Tente novamente para consultar o payload atualizado de preço.
      </p>
      <LaisPrimaryButton
        class="mt-3"
        variant="ghost"
        icon="i-lucide-refresh-cw"
        @click="fetchReport"
      >
        Tentar novamente
      </LaisPrimaryButton>
    </div>

    <template v-else-if="report">
      <div
        v-if="hasInsufficientData && !rulerState.available"
        class="rounded-lais border border-amber-200 bg-[#fff8e1] p-4 text-[13px] text-amber-900"
      >
        <p class="font-semibold">
          Temos poucos dados para estimar o preço deste imóvel
        </p>
        <p class="mt-1">
          O relatório fica disponível assim que houver comparáveis suficientes
          para a região e a tipologia.
        </p>
      </div>

      <PricingReportSection
        v-if="rulerState.available"
        section-key="price_estimated_ruler"
        title="Faixa de preço estimada"
        subtitle="Percentis de mercado, mediana e marcador principal do relatório."
        icon="i-lucide-gauge"
        @viewed="handleSectionViewed"
      >
        <div class="grid gap-3">
          <component
            :is="priceRangeComponent"
            :min="report.min_price"
            :max="report.max_price"
          />
          <PriceRuler
            :value="report.main_marker_price"
            :min="report.min_price"
            :max="report.max_price"
            :percentiles="report.percentiles"
            :marker-color="report.main_marker_color"
            :variant="rulerVariant"
            :format-value="formatCurrency"
          />
        </div>
      </PricingReportSection>

      <PricingReportSection
        v-if="hasCmaReport"
        section-key="cma_report"
        title="Laudo CMA versionado"
        subtitle="Fonte, data-base, versão e percentis usados no laudo compartilhável."
        icon="i-lucide-file-check-2"
        @viewed="handleSectionViewed"
      >
        <div class="grid gap-3">
          <div class="grid gap-3 sm:grid-cols-3">
            <div
              v-for="percentile in [
                { label: 'P25', value: cmaPercentiles.p25 },
                { label: 'P50', value: cmaPercentiles.p50 },
                { label: 'P75', value: cmaPercentiles.p75 },
              ]"
              :key="percentile.label"
              class="rounded-lais-sm border border-lais-border-soft bg-lais-bg-page px-3 py-3"
            >
              <p
                class="text-[11px] font-semibold uppercase text-lais-text-muted"
              >
                {{ percentile.label }}
              </p>
              <p class="mt-1 text-[16px] font-semibold text-lais-text">
                {{ formatCurrency(percentile.value) }}
              </p>
            </div>
          </div>

          <div
            class="grid gap-2 rounded-lais-sm border border-lais-border-soft bg-lais-bg-page px-3 py-3 text-[12px] text-lais-text-muted sm:grid-cols-2"
          >
            <p>
              Versão:
              <strong class="text-lais-text">
                v{{ cmaLatestReport.version_number || '0' }}
              </strong>
            </p>
            <p>
              Fonte:
              <strong class="text-lais-text">
                {{ cmaReport.source?.source || 'CMA local' }}
              </strong>
            </p>
            <p>
              Data:
              <strong class="text-lais-text">
                {{
                  formatDate(cmaReport.base_date || cmaReport.source?.data_date)
                }}
              </strong>
            </p>
            <p>
              Confiança:
              <strong class="text-lais-text">{{ cmaConfidenceLabel }}</strong>
            </p>
          </div>

          <div v-if="cmaCriteriaEntries.length" class="flex flex-wrap gap-2">
            <span
              v-for="criterion in cmaCriteriaEntries"
              :key="criterion.label"
              class="rounded-full border border-lais-border-soft bg-white px-3 py-1.5 text-[12px] font-semibold text-lais-text"
            >
              {{ criterion.label }}: {{ criterion.value }}
            </span>
          </div>
        </div>
      </PricingReportSection>

      <PricingReportSection
        v-if="cmaComparables.length"
        section-key="cma_comparables"
        title="Comparáveis do laudo"
        subtitle="Amostra, ajustes, motivo de decisão e score de comparabilidade."
        icon="i-lucide-git-compare-arrows"
        @viewed="handleSectionViewed"
      >
        <div class="grid gap-3">
          <article
            v-for="item in cmaComparables.slice(0, 8)"
            :key="
              item.observation_id ||
              item.external_id ||
              `${cmaComparableSource(item)}-${cmaComparableDate(item)}`
            "
            class="rounded-lais-sm border border-lais-border-soft bg-lais-bg-page px-3 py-3"
          >
            <div class="flex flex-wrap items-start justify-between gap-3">
              <div class="min-w-0">
                <p class="text-[13px] font-semibold text-lais-text">
                  {{ formatCurrency(cmaComparablePrice(item)) }}
                </p>
                <p class="mt-1 text-[12px] text-lais-text-muted">
                  {{ cmaComparableSource(item) }} ·
                  {{ formatDate(cmaComparableDate(item)) }} · score
                  {{ cmaComparableScore(item) }}
                </p>
                <p
                  v-if="item.decision_reason"
                  class="mt-1 text-[12px] text-lais-text-muted"
                >
                  Motivo: {{ item.decision_reason }}
                </p>
              </div>
              <span
                class="rounded-full px-2.5 py-1 text-[11px] font-semibold"
                :class="
                  item.selection_status === 'included'
                    ? 'bg-emerald-50 text-emerald-800'
                    : 'bg-amber-50 text-amber-800'
                "
              >
                {{ item.selection_status || 'comparável' }}
              </span>
            </div>
          </article>
        </div>
      </PricingReportSection>

      <PricingReportSection
        v-if="cmaVersions.length || cmaDisclaimers.length"
        section-key="cma_versions"
        title="Versões e ressalvas"
        subtitle="Histórico append-only do laudo e limites de uso do CMA."
        icon="i-lucide-history"
        @viewed="handleSectionViewed"
      >
        <div class="grid gap-3">
          <div v-if="cmaVersions.length" class="grid gap-2">
            <div
              v-for="version in cmaVersions.slice(0, 5)"
              :key="version.id"
              class="flex flex-wrap items-center justify-between gap-3 rounded-lais-sm border border-lais-border-soft bg-lais-bg-page px-3 py-2 text-[12px]"
            >
              <span class="text-lais-text-muted">
                v{{ version.version_number }} · {{ version.status }} ·
                {{ formatDate(version.issued_at) }}
              </span>
              <strong class="text-lais-text">
                {{ formatCurrency(version.recommended_amount) }}
              </strong>
            </div>
          </div>
          <ul
            v-if="cmaDisclaimers.length"
            class="grid gap-1 rounded-lais-sm bg-lais-bg-muted px-3 py-2 text-[12px] text-lais-text-muted"
          >
            <li v-for="item in cmaDisclaimers" :key="item">• {{ item }}</li>
          </ul>
        </div>
      </PricingReportSection>

      <PricingReportSection
        v-if="filterTags.length"
        section-key="price_filter_tags"
        title="Filtros aplicados"
        subtitle="Recorte usado para encontrar comparáveis de preço."
        icon="i-lucide-list-filter"
        @viewed="handleSectionViewed"
      >
        <div class="flex flex-wrap gap-2">
          <span
            v-for="tag in filterTags"
            :key="tag.key"
            class="rounded-full border border-lais-border-soft bg-lais-bg-page px-3 py-1.5 text-[12px] font-semibold text-lais-text"
          >
            {{ tag.label }}
          </span>
        </div>
      </PricingReportSection>

      <PricingReportSection
        v-if="similarProperties.length"
        section-key="similar_properties"
        title="Imóveis similares"
        subtitle="Comparáveis usados para orientar a faixa sugerida."
        icon="i-lucide-building-2"
        @viewed="handleSectionViewed"
      >
        <div class="grid gap-3">
          <article
            v-for="item in similarProperties"
            :key="item.id || item.code || similarTitle(item)"
            class="rounded-lais-sm border border-lais-border-soft bg-lais-bg-page px-3 py-3"
          >
            <div class="flex flex-wrap items-start justify-between gap-3">
              <div class="min-w-0">
                <p class="text-[13px] font-semibold text-lais-text">
                  {{ similarTitle(item) }}
                </p>
                <p
                  v-if="similarDetails(item)"
                  class="mt-1 text-[12px] text-lais-text-muted"
                >
                  {{ similarDetails(item) }}
                </p>
              </div>
              <strong class="text-[13px] text-lais-text">
                {{ formatCurrency(similarPrice(item)) }}
              </strong>
            </div>
          </article>
        </div>
      </PricingReportSection>

      <PricingReportSection
        v-if="medianCards.length"
        section-key="median_cards"
        title="Resumo do mercado"
        subtitle="Indicadores consolidados do cálculo de preço."
        icon="i-lucide-layout-dashboard"
        @viewed="handleSectionViewed"
      >
        <div class="grid gap-3 sm:grid-cols-2">
          <div
            v-for="card in medianCards"
            :key="card.label"
            class="rounded-lais-sm border border-lais-border-soft bg-lais-bg-page px-3 py-3"
          >
            <p class="text-[11px] font-semibold uppercase text-lais-text-muted">
              {{ card.label }}
            </p>
            <p class="mt-1 text-[16px] font-semibold text-lais-text">
              {{ formattedMedianCard(card) }}
            </p>
          </div>
        </div>
      </PricingReportSection>

      <PricingReportSection
        v-if="propertyHistory.length"
        section-key="property_history"
        title="Histórico de preços"
        subtitle="Linha do tempo sanitizada com registros de mercado e da Imudei."
        icon="i-lucide-history"
        @viewed="handleSectionViewed"
      >
        <ol class="grid gap-3">
          <li
            v-for="item in propertyHistory"
            :key="`${historySource(item)}-${historyDate(item)}-${historyPrice(item)}`"
            class="rounded-lais-sm border border-lais-border-soft bg-lais-bg-page px-3 py-3"
          >
            <div class="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p class="text-[13px] font-semibold text-lais-text">
                  {{ formatCurrency(historyPrice(item)) }}
                </p>
                <p class="mt-1 text-[12px] text-lais-text-muted">
                  {{ historyKind(item) }} · {{ historySource(item) }} ·
                  {{ formatDate(historyDate(item)) }}
                </p>
              </div>
            </div>
          </li>
        </ol>
      </PricingReportSection>

      <PricingReportSection
        v-if="showEarningEstimated"
        section-key="earning_estimated"
        title="Renda estimada"
        subtitle="Projeção anual calculada a partir do marcador principal."
        icon="i-lucide-trending-up"
        @viewed="handleSectionViewed"
      >
        <div class="rounded-lais-sm bg-lais-primary-50 px-4 py-4">
          <p class="text-[12px] font-semibold text-lais-primary">
            Potencial anual
          </p>
          <p class="mt-1 text-[24px] font-semibold text-lais-text">
            {{ formatCurrency(report.earning_estimated) }}
          </p>
        </div>
      </PricingReportSection>
    </template>
  </div>
</template>
